import { Modal, Popover, Table, Tag, Typography } from 'antd';
import { Activity, useState } from 'react';
import { getCustomerGlobalSearch } from '../../../redux/actions/customers';
import SearchInput from '../SearchInput';
import { formatAddress, formatCustomerDays, getAddressType, getAddressTypeIndicator } from '../../../utils';
import { Info } from 'lucide-react';
import clsx from 'clsx';
import { getRoutes } from '../../../routes/Routes';
import { Link } from 'react-router-dom';

const { Text, Title } = Typography;

const GlobalSearch = () => {
    const [visible, setVisible] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

    const handleSearch = async ({ searchText, page = 1, loading }) => {
        const value = searchText?.trim() ?? '';

        setSearchValue(value);

        if (value.length < 3) {
            setCustomers([]);
            setVisible(false);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                totalRecords: 0,
            }));
            return;
        }

        setLoading(true);

        try {
            const res = await getCustomerGlobalSearch(
                {
                    searchText,
                    page,
                },
                loading
            );

            if (res.ack) {
                setVisible(true);

                const enrichedCustomers = (res.customers || []).map((customer) => {
                    const { deliveryDays } = formatCustomerDays(customer || {});
                    return {
                        ...customer,
                        deliveryDays,
                    };
                });

                setCustomers(enrichedCustomers);

                setPagination((prev) => ({
                    ...prev,
                    current: page,
                    totalRecords: res.total_count,
                    totalPages: Math.ceil(res.total_count / prev.pageSize),
                }));
            }
            return res;
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Customer',
            dataIndex: 'title',
            key: 'title',
            width: 300,
            render: (_, record) => (
                <Link to={getRoutes().customer_details.route(record?.customer_netsuite_id)} onClick={() => {
                    setVisible(false);
                    setSearchValue('');
                }}>
                    {record.code} - {record.title}
                </Link>
            ),
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Sales Rep',
            dataIndex: 'salesrep',
            key: 'salesrep',
        },
        {
            title: 'Credit Controller',
            dataIndex: 'credit_controller',
            key: 'credit_controller',
        },
        {
            title: 'Price Level',
            dataIndex: 'price_level',
            key: 'price_level',
        },
        {
            title: 'Terms',
            dataIndex: 'terms',
            key: 'terms',
        },
        {
            title: 'Delivery Days',
            dataIndex: 'deliveryDays',
            key: 'deliveryDays',
            render: (deliveryDays) => {
                if (!deliveryDays || deliveryDays?.length === 0) return '-';
                return deliveryDays?.map((day) => {
                    return (
                        <Tag color="gray" key={day?.value} className="mr-1">
                            {day?.label}
                        </Tag>
                    );
                });
            },
        },
        {
            title: 'Postcode',
            dataIndex: 'postcode',
            key: 'postcode',
            render: (postcode, record) => {
                const { color } = getAddressTypeIndicator(record);
                const addressType = getAddressType(record);
                const { fullAddress } = formatAddress(record || {});
                return (
                    <div className="flex align-items-center justify-content-between gap-1 cursor-pointer">
                        {postcode || '-'}{' '}
                        {addressType && fullAddress && (
                            <Popover content={fullAddress} title={addressType}>
                                <Tag color={color} className='flex align-items-center gap-1'>
                                    {addressType}{' '}
                                    <Info size={16} color='white' />
                                </Tag>
                            </Popover>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="global-search-container flex-1 flex justify-content-center">
            <SearchInput
                className="w-full max-w-30rem"
                placeholder="Search Customers (min 3 characters) by Name, Code, Phone, or Postcode"
                value={searchValue}
                onSearch={(v) => handleSearch({ searchText: v, page: 1 })}
            />

            <Activity mode={!visible || searchValue?.length === 0 ? 'hidden' : 'visible'}>
                <Modal
                    title={
                        <div className="flex flex-column">
                            <Title level={5} className="m-0 text-bold">
                                Search Customer
                            </Title>
                            <Text type="secondary">
                                Search Customers (min 3 characters) by Name, Code, Phone, or Postcode
                            </Text>
                        </div>
                    }
                    open={visible}
                    onCancel={() => {
                        setVisible(false);
                        setSearchValue('');
                    }}
                    width="90VW"
                    centered
                    className="global-search-modal"
                    footer={null}
                >
                    <div className="mb-4">
                        <SearchInput
                            className="w-full modal-search-input"
                            placeholder="Search Customers (min 3 characters) by Name, Code, Phone, or Postcode"
                            value={searchValue}
                            onSearch={(v) => handleSearch({ searchText: v, page: 1, loading: false })}
                            autofocus
                        />
                    </div>

                    <div className="search-results-wrapper">
                        <Table
                            size="small"
                            columns={columns}
                            rowKey={(record) => record?.customer_netsuite_id}
                            dataSource={customers}
                            loading={loading}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.totalRecords,
                                showSizeChanger: false,
                                responsive: true,
                                size: 'small',
                                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
                                onChange: (page) => handleSearch({ page, searchText: searchValue }),
                            }}
                            scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                            components={{
                                body: {
                                    cell: ({ className, ...rest }) => (
                                        <td
                                            {...rest}
                                            className={customers?.length && clsx(className, 'py-1')}
                                        />
                                    ),
                                },
                            }}
                        />
                    </div>
                </Modal>
            </Activity>
        </div>
    );
};

export default GlobalSearch;
